import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Sparkles, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Custom = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    flagType: '',
    size: '',
    quantity: '',
    description: '',
    deadline: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(false);
    
    // Send to backend API
    fetch(`${BACKEND_URL}/api/orders/custom-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      setSubmitted(true);
      toast.success('Custom order request submitted! Check your email at coastalflagco@yahoo.com');
    })
    .catch(error => {
      console.error('Error:', error);
      toast.error('Failed to submit request. Please call 205-587-2402');
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Request Received!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your custom order request. Our team will review your requirements and contact you within 24 hours with a quote and timeline.
            </p>
            <Button onClick={() => window.location.href = '/'} className="bg-red-600 hover:bg-red-700">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-white py-12 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-5xl font-bold text-slate-900 mb-4">Custom Flag Design</h1>
            <p className="text-xl text-slate-600">
              Bring your vision to life with our custom flag design service. Perfect for businesses, organizations, events, and special occasions.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Form */}
          <Card>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Custom Order Form</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="205-555-0123"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="flagType">Flag Type *</Label>
                  <Input
                    id="flagType"
                    name="flagType"
                    value={formData.flagType}
                    onChange={handleChange}
                    required
                    placeholder="Business, Organization, Event, etc."
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="size">Size *</Label>
                    <Input
                      id="size"
                      name="size"
                      value={formData.size}
                      onChange={handleChange}
                      required
                      placeholder="3x5 ft"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      placeholder="1"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="deadline">Deadline (Optional)</Label>
                  <Input
                    id="deadline"
                    name="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Design Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Please describe your design requirements, colors, logos, text, etc."
                    className="mt-2 min-h-[120px]"
                  />
                </div>

                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-red-500 transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Upload design files or images (optional)</p>
                  <p className="text-xs text-slate-500 mt-1">Drag and drop or click to browse</p>
                </div>

                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-lg py-6">
                  Submit Custom Order Request
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="space-y-6">
            <Card className="bg-slate-900 text-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">How It Works</h3>
                <ol className="space-y-4">
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center font-bold">1</span>
                    <div>
                      <h4 className="font-semibold mb-1">Submit Your Request</h4>
                      <p className="text-slate-300 text-sm">Fill out the form with your design requirements</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center font-bold">2</span>
                    <div>
                      <h4 className="font-semibold mb-1">Get a Quote</h4>
                      <p className="text-slate-300 text-sm">We'll review and send you a detailed quote within 24 hours</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center font-bold">3</span>
                    <div>
                      <h4 className="font-semibold mb-1">Design Approval</h4>
                      <p className="text-slate-300 text-sm">Review and approve the design mockup</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center font-bold">4</span>
                    <div>
                      <h4 className="font-semibold mb-1">Production & Delivery</h4>
                      <p className="text-slate-300 text-sm">Your custom flag is manufactured and shipped</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">What We Can Create</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Company & business flags
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Organization & club flags
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Event & celebration flags
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Sports team flags
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Family crest flags
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Promotional flags
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-red-600 text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-2">Need Help?</h3>
                <p className="mb-4">Call us for immediate assistance</p>
                <a href="tel:205-587-2402" className="text-3xl font-bold hover:underline">
                  205-587-2402
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Custom;